import { isErrorEnvelope, type ErrorEnvelope } from '@lib/services/shopify/scopeError';

/**
 * Infra 1.5 #22 / #23 —— Shopify「连接断了」的<b>唯一判定点</b>与广播口。
 *
 * <p>PRD #23 的要求是「前端<b>统一拦截</b> Shopify 相关错误码，命中就就地渲染那一句兜底文案」，
 * 并明确<b>不做逐位置空态改造</b>（依赖 Shopify 的位置有三十多处，逐个改会满屏都是这句）。
 * 所以判定只能有一份 —— 就是这个文件；广播出去后由 `shopifyConnectionStore` 收，页面只读 store。
 *
 * <p><b>为什么按 subtype 判定，不按错误文案。</b>沿用 `scopeError.ts` 立下的规矩：
 * 那里原本是对英文句子做子串匹配，结果一改文案就静默失效。所以这里也只认 `subtype`，
 * 错误码只作为兜底信号。
 *
 * <p><b>为什么不在这里改拦截器的两条路。</b>V3 的 E6 记过：`api.ts` 有两条方向相反的分支 ——
 * 成功响应里带 `{errorCode, errorMessage}` 信封的会 throw，真 HTTP 非 401/502 的失败被改写成
 * `status: 200` 后 resolve。把后者也改成 reject 是<b>正确的终局</b>，但那会让全仓 38 处
 * `orThrow` / `isErrorEnvelope` 的调用点行为翻转，是一次独立的清扫，不能夹在这条需求里做。
 * <b>本次的做法是：两条路都在返回之前经过这里</b>，判定与广播只有一份，各页面不再自己判连接状态；
 * 两条路各自的 resolve / reject 行为<b>一个字没动</b>，所以没有回归面。
 *
 * <p><b>后端要给的东西（唯一一件）</b>：Shopify 连接失效时，错误信封里带上下面其中一个 `subtype`。
 * 现在只能靠错误码 + 文案兜底判断，会漏也会误判。
 */

/**
 * 判定为「未连接」的 subtype。
 *
 * <p><b>已和后端对齐（2026-09-17）。</b> 此前这里是四个按命名习惯<b>猜</b>的字符串
 * （`shopify_app_uninstalled` / `shopify_token_revoked` / `shopify_shop_changed` /
 * `shopify_not_connected`），而后端 `ErrorTypes.IntegrationError` 实际只发两个码，
 * <b>一个都对不上</b> —— 也就是说这条拦截链此前恒不命中。
 *
 * <p>后端把 PRD #22 第三节那四种情况（`app/uninstalled`、token 被吊销、shop id 变了、
 * 自愈重试持续失败）<b>合并成了一个对外码</b>：商家要做的事都是同一件，分四个码只会让
 * 前端多写三个等价分支。
 *
 * <p>⚠️ <b>`shopify_upstream_unavailable` 绝不能进这个集合。</b> 那是「我们或 Shopify 暂时
 * 读不到」，商家修不了 —— 让一个连得好好的商家去重连，换来的是一次不可逆的清库初始化。
 * 它由 `SHOPIFY_COPY.emptyState` 之外的那句「请稍后再试」承接。
 */
export const SHOPIFY_DISCONNECT_SUBTYPES = new Set(['shopify_disconnected']);

/**
 * 兜底信号：没有 subtype 时，按「Shopify 相关 ＋ 授权类错误码」判。
 *
 * <p>刻意收得很窄：401 / 403 才算，400 不算 —— PRD #22 的 Case 1 表现就是「400 Bad Request on the
 * OAuth token endpoint」，但 400 也是一大堆参数错误的通用码，凭它判断整站未连接会误伤。
 * 宁可漏判（商家点到时看到具体报错），也不要误判（整站弹「连接已中断」）。
 */
const FALLBACK_CODES = new Set([401, 403]);
const SHOPIFY_HINT = /shopify/i;

function envelopeSaysDisconnected(env: ErrorEnvelope): boolean {
  if (env.subtype && SHOPIFY_DISCONNECT_SUBTYPES.has(env.subtype)) return true;
  return FALLBACK_CODES.has(env.errorCode) && SHOPIFY_HINT.test(env.errorMessage ?? '');
}

/** 判定入口。传进来的可以是响应 body、也可以是 axios error 的 `response.data`。 */
export function isShopifyDisconnected(data: unknown): boolean {
  if (!isErrorEnvelope(data)) return false;
  return envelopeSaysDisconnected(data);
}

// ─── 广播 ────────────────────────────────────────────────────────────────
//
// `api.ts` 只 import 下面两个函数，不 import store —— store 会 import api（拉连接状态），
// 直接引用就成了循环依赖。回调注册表把方向掰直：api 只管喊，store 自己来听。

type Listener = () => void;
const listeners = new Set<Listener>();

export function onShopifyDisconnected(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/**
 * 广播「连接断了」。
 *
 * <p>由 `api.ts` 的响应拦截器在两条路上各调一次。<b>幂等</b>：store 那边已经是未连接就什么都不做，
 * 所以一屏十个并发请求同时命中也只会弹一次窗。
 */
export function emitShopifyDisconnected(): void {
  for (const listener of listeners) {
    try {
      listener();
    } catch {
      // 一个监听者抛错不能影响拦截器把响应交回给调用方。
    }
  }
}

/**
 * #23 的那一句兜底文案 —— <b>全站唯一一份</b>。
 *
 * <p>取自 connect demo 的实际字串（不是 PRD 里的中文口径：产品界面是英文，照抄中文会在英文页面里
 * 塞中文）。弹窗、首页提示条、就地空态三处都从这里取，改文案只改这里。
 */
export const SHOPIFY_COPY = {
  dialogTitle: 'Connection Interrupted',
  dialogBody:
    'The connection between YepAI and Shopify has been interrupted. You may run into problems while using YepAI. Please go to Settings > Integrations to reconnect.',
  dialogDismiss: 'Got It',
  dialogAction: 'Reconnect',
  bannerTitle: 'Shopify is not connected',
  bannerAction: 'Reconnect',
  emptyState: "Shopify isn't connected, so there's no data here yet.",
  emptyStateAction: 'Reconnect',
} as const;
